import { ReactNode, useEffect, useMemo } from 'react';
import { useGetNoSelectedItemsLabel } from 'src/components/common/MultiSelect/useGetNoSelectedItemsLabel';
import { useContent } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import {
  filterPoSelectionIds,
  filterTgSelectionIds,
} from 'src/contexts/MultiSelectionContext/MultiSelectionContext.utils';
import { ContentId } from 'src/utils/constants/contentId';

interface SetPurchasesTimeViewSelectionCountLabelProps {
  children: ReactNode;
}

export const SetPurchasesTimeViewSelectionCountLabel = ({
  children,
}: SetPurchasesTimeViewSelectionCountLabelProps) => {
  const purchaseText = useContent(ContentId.Purchase);
  const purchasesText = useContent(ContentId.Purchases);
  const ticketGroupText = useContent(ContentId.TicketGroup);
  const ticketGroupsText = useContent(ContentId.TicketGroups);
  const selectedText = useContent(ContentId.Selected);
  const {
    setSelectedCountLabel,
    totalItemsSelected,
    getSelection,
    setUsingCustomSelectedCountLabel,
    getGroupSelectableGroupMetadata,
  } = useMultiSelectionContext();

  const noSelectedItemsLabel = useGetNoSelectedItemsLabel();

  useEffect(() => {
    setUsingCustomSelectedCountLabel(true);
  }, [setUsingCustomSelectedCountLabel]);

  const selectedCountLabel = useMemo<string>(() => {
    if (!totalItemsSelected) {
      return noSelectedItemsLabel;
    }

    const selection = getSelection();

    // Separate PO selections (simple IDs) from TG selections (packed IDs with comma)
    const poSelections = filterPoSelectionIds(selection.items.itemIds);
    const tgSelections = filterTgSelectionIds(selection.items.itemIds);

    let purchasesCount = poSelections.length;
    const ticketGroupsCount = tgSelections.length;

    selection.groupIds.forEach((groupId) => {
      const g = getGroupSelectableGroupMetadata(groupId);
      purchasesCount += g?.itemCount ?? 0;
    });

    // If we have TG selections, show ticket group count
    // If we have PO selections, show purchase count
    // If we have both, show both
    const parts: string[] = [];

    if (purchasesCount > 0) {
      const purchaseLabel = purchasesCount === 1 ? purchaseText : purchasesText;
      parts.push(`${purchasesCount} ${purchaseLabel}`);
    }

    if (ticketGroupsCount > 0) {
      const tgLabel =
        ticketGroupsCount === 1 ? ticketGroupText : ticketGroupsText;
      parts.push(`${ticketGroupsCount} ${tgLabel}`);
    }

    return `${parts.join(', ')} ${selectedText}`.toLocaleLowerCase();
  }, [
    totalItemsSelected,
    getSelection,
    purchaseText,
    purchasesText,
    ticketGroupText,
    ticketGroupsText,
    selectedText,
    noSelectedItemsLabel,
    getGroupSelectableGroupMetadata,
  ]);

  useEffect(() => {
    setSelectedCountLabel(selectedCountLabel);
  }, [selectedCountLabel, setSelectedCountLabel]);

  return <>{children}</>;
};
